/**
 * Office selector state for the whole app (ADR-0012). The URL's `?office=`
 * is the source of truth; this provider fills it in on first load from the
 * user's remembered choice and remembers every change (server preference
 * for the user, local storage for this device).
 *
 * Every office-scoped query goes through `useOfficeQuery`, which keys the
 * cache on the selector value and sends it to the API, so switching office
 * re-scopes every screen at once.
 */
import type { LocationScope, OfficeScopeMode } from '@velora/contracts';
import type { ShellBootstrap, ShellOffice } from '@velora/contracts/shell';
import { putShellPreferences } from '@velora/sdk';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { api } from '../api.js';
import { readString, writeString } from '../lib/storage.js';
import {
  initialScope,
  localScopeKey,
  locationIdsFor,
  OFFICE_PARAM,
  scopeLabel,
  scopeToParam,
} from './scope.js';

export interface OfficeScopeValue {
  scope: LocationScope;
  /** `all` or an office id: the URL param and API header value. */
  param: string;
  offices: readonly ShellOffice[];
  /** The selected office, when one is selected. */
  office: ShellOffice | undefined;
  /** Offices in scope (all granted offices in "All offices"). */
  locationIds: readonly string[];
  isAll: boolean;
  /** "All 3 offices" or the office name. */
  label: string;
  /** `replace` swaps the history entry (an automatic default, not a user pick). */
  setScope: (scope: LocationScope, opts?: { replace?: boolean }) => void;
  /** Colour slot of an office (stable across charts). */
  colorIndexOf: (locationId: string) => number;
}

const EMPTY: OfficeScopeValue = {
  scope: { kind: 'all' },
  param: 'all',
  offices: [],
  office: undefined,
  locationIds: [],
  isAll: true,
  label: 'No offices',
  setScope: () => undefined,
  colorIndexOf: () => 0,
};

const OfficeScopeContext = createContext<OfficeScopeValue>(EMPTY);

export function useOfficeScope(): OfficeScopeValue {
  return useContext(OfficeScopeContext);
}

/** Lets tests and stories provide a fixed scope. */
export const OfficeScopeTestProvider = OfficeScopeContext.Provider;

export function OfficeScopeProvider({
  session,
  personScope = null,
  children,
}: {
  session: ShellBootstrap;
  /** The selection identity holds for this person (picked at sign-in), as a param. */
  personScope?: string | null;
  children: ReactNode;
}) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const location = useLocation();
  const navigate = useNavigate();
  const urlParam =
    typeof search[OFFICE_PARAM] === 'string' ? (search[OFFICE_PARAM] as string) : null;
  const key = localScopeKey(session.practice.id, session.user.id);

  const scope = useMemo(
    () =>
      initialScope({
        urlParam,
        personPref: personScope,
        serverPref: session.preferences.officeScope,
        localPref: readString(key),
        offices: session.offices,
        defaultLocationId: session.defaultLocationId,
      }),
    [urlParam, personScope, session, key],
  );
  const param = scope ? scopeToParam(scope) : 'all';

  // Keep the URL in step (first load, or an office the user lost access to).
  useEffect(() => {
    if (scope && urlParam !== param) {
      void navigate({
        to: location.pathname,
        search: (prev: Record<string, unknown>) => ({ ...prev, [OFFICE_PARAM]: param }),
        replace: true,
      } as never);
    }
  }, [scope, urlParam, param, navigate, location.pathname]);

  // Remember the choice: this device at once, the server after a pause.
  const savedRef = useRef(session.preferences.officeScope ?? null);
  useEffect(() => {
    if (!scope) return undefined;
    writeString(key, param);
    if (savedRef.current === param) return undefined;
    const t = setTimeout(() => {
      savedRef.current = param;
      putShellPreferences(api, { body: { officeScope: param } }).catch(() => {
        // Not fatal: the device copy still remembers it.
        savedRef.current = null;
      });
    }, 600);
    return () => clearTimeout(t);
  }, [scope, param, key]);

  const setScope = useCallback(
    (next: LocationScope, opts?: { replace?: boolean }) => {
      void navigate({
        to: location.pathname,
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          [OFFICE_PARAM]: scopeToParam(next),
        }),
        ...(opts?.replace ? { replace: true } : {}),
      } as never);
    },
    [navigate, location.pathname],
  );

  const value = useMemo<OfficeScopeValue>(() => {
    if (!scope) return { ...EMPTY, offices: session.offices };
    const colors = new Map(session.offices.map((o) => [o.id, o.colorIndex]));
    return {
      scope,
      param,
      offices: session.offices,
      office:
        scope.kind === 'location'
          ? session.offices.find((o) => o.id === scope.locationId)
          : undefined,
      locationIds: locationIdsFor(scope, session.offices),
      isAll: scope.kind === 'all',
      label: scopeLabel(scope, session.offices),
      setScope,
      colorIndexOf: (id) => colors.get(id) ?? 0,
    };
  }, [scope, param, session.offices, setScope]);

  return <OfficeScopeContext.Provider value={value}>{children}</OfficeScopeContext.Provider>;
}

export type { OfficeScopeMode };
